import React, { forwardRef } from 'react';
import { ButtonBase } from './ButtonBase.js';
import { TooltipContext, Tooltip } from '../TooltipV2/Tooltip.js';
import { TooltipContext as TooltipContext$1 } from '../Tooltip/Tooltip.js';
import classes from './ButtonBase.module.css.js';
import { clsx } from 'clsx';
import { jsx } from 'react/jsx-runtime';

const IconButton = /*#__PURE__*/forwardRef(({
  icon: Icon,
  'aria-label': ariaLabel,
  description,
  disabled,
  tooltipDirection,
  // This is planned to be a temporary prop until the default tooltip on icon buttons are fully rolled out.
  unsafeDisableTooltip = false,
  keyshortcuts,
  keybindingHint,
  className,
  ...props
}, forwardedRef) => {
  // If the icon button is already wrapped in a tooltip, do not add one.
  const {
    tooltipId
  } = React.useContext(TooltipContext); // Tooltip v2
  const {
    tooltipId: tooltipIdV1
  } = React.useContext(TooltipContext$1); // Tooltip v1

  const {
    'aria-expanded': isExpanded,
    'aria-haspopup': hasPopup
  } = props;
  const hasExternalTooltip = tooltipId || tooltipIdV1;

  // If the button has an active "popup" (like a menu), we don't want to show the tooltip.
  // This is mostly for `ActionMenu`, but could be applicable elsewhere.
  const hasActivePopup = (isExpanded === true || isExpanded === 'true') && hasPopup === 'true';
  const withoutTooltip = unsafeDisableTooltip || disabled || ariaLabel === undefined || ariaLabel === '' || hasExternalTooltip;
  if (withoutTooltip) {
    return /*#__PURE__*/jsx(ButtonBase, {
      icon: Icon,
      className: clsx(className, classes.IconButton),
      "data-component": "IconButton",
      type: "button",
      "aria-label": ariaLabel,
      disabled: disabled,
      ...props,
      // @ts-expect-error StyledButton wants both Anchor and Button refs
      ref: forwardedRef
    });
  } else {
    const tooltipText = description !== null && description !== void 0 ? description : ariaLabel;
    return /*#__PURE__*/jsx(Tooltip, {
      ref: forwardedRef,
      text: tooltipText,
      type: description ? undefined : 'label',
      direction: tooltipDirection,
      keybindingHint: keybindingHint !== null && keybindingHint !== void 0 ? keybindingHint : keyshortcuts,
      _privateDisableTooltip: hasActivePopup,
      children: /*#__PURE__*/jsx(ButtonBase, {
        icon: Icon,
        className: clsx(className, classes.IconButton),
        "data-component": "IconButton",
        type: "button",
        "aria-keyshortcuts": keyshortcuts !== null && keyshortcuts !== void 0 ? keyshortcuts : undefined
        // If description is provided, we will use the tooltip to describe the button, so we need to keep the aria-label to label the button.
        ,
        "aria-label": description ? ariaLabel : undefined,
        ...props
      })
    });
  }
});

export { IconButton };
